import React, { useEffect, useRef, useState } from 'react';
import LogoTransparent from '../../../assets/svg/logoTransparent.svg';
import { connect } from 'react-redux';
import { eulaConfirm, logOut } from 'store/actions/auth';
import { error } from 'components/toast';
import DOMPurify from "dompurify";

const Eula = ({
  open = true,
  title = 'END USER LICENSE AGREEMENT (EULA) FOR OPUS',
  subtext = 'Please review the agreement below to continue using Opus.',
  data,
  triggerLogout,
  triggerEulaConfirm,
  onSuccess,
  setShowEULA
}) => {
  const contentRef = useRef(null);
  const [canAgree, setCanAgree] = useState(false);

  useEffect(() => {
    const el = contentRef.current;
    if (!el) return;

    const checkScrollEnd = () => {
      const threshold = 2;

      const reachedEnd =
        el.scrollTop + el.clientHeight >=
        el.scrollHeight - threshold;

      setCanAgree(reachedEnd);
    };

    checkScrollEnd();

    el.addEventListener('scroll', checkScrollEnd, {
      passive: true,
    });

    window.addEventListener('resize', checkScrollEnd);

    return () => {
      el.removeEventListener('scroll', checkScrollEnd);
      window.removeEventListener('resize', checkScrollEnd);
    };
  }, [open, data]);

  if (!open) return null;

  const onDisagree = () => {
    triggerLogout();
    setShowEULA(false);
  };

  const handleAgree = () => {
    if (!canAgree) return;
    const payload = {
      onSuccess: async (loginData) => {
        onSuccess(loginData);
      },
      onError: (msg) => {
        error(msg)
      },
    }
    triggerEulaConfirm(payload);
  };

  return (
    <div className="flex h-screen flex-col overflow-hidden bg-[#F7F7F9]">
      <header className="flex shrink-0 justify-center pt-8">
        <img
          className="h-[120px] object-contain bg-transparent"
          src={LogoTransparent}
          alt="people events logo"
        />
      </header>

      <div className="flex flex-1 items-start justify-center overflow-hidden p-6">
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="eula-title"
          className="flex h-[calc(100vh-220px)] min-h-[520px] max-h-[640px] w-full max-w-[860px] flex-col overflow-hidden rounded-[22px] border border-[#E8E8EC] bg-white shadow-[0_20px_45px_rgba(15,23,42,0.08)]"
        >
          <div className="shrink-0 p-4 text-center">
            <h2
              id="eula-title"
              className="text-[22px] font-bold tracking-[0.02em] text-[#1F1F24]"
            >
              {data?.title || title}
            </h2>

            <p className="mt-3.5 text-base leading-[1.6] text-gray-500">
              {subtext}
            </p>
          </div>

          <div className="flex min-h-0 flex-1 px-9 pb-4">
            <div
              ref={contentRef}
              className="min-h-0 flex-1 overflow-y-auto rounded-2xl border border-[#DADCE2] bg-[#FAFAFB] p-[30px]"
            >
              <div
                className="eula-content text-sm leading-7 text-gray-700"
                dangerouslySetInnerHTML={{
                  __html: DOMPurify.sanitize(data?.content || ""),
                }}
              />
            </div>
          </div>

          <div className="shrink-0 px-9 pb-4">
            <div className="flex justify-end gap-3.5 border-t border-[#ECECF1] pt-4">
              <button
                type="button"
                onClick={onDisagree}
                className="h-12 rounded-xl border border-gray-300 bg-white px-6 text-[15px] font-medium text-gray-700 transition-colors hover:bg-gray-50"
              >
                I Do Not Agree
              </button>

              <button
                type="button"
                onClick={handleAgree}
                disabled={!canAgree}
                className={`h-12 min-w-[150px] rounded-xl px-6 text-[15px] font-bold text-white transition-all ${
                  canAgree
                    ? 'bg-gradient-to-r from-[#7C4DFF] to-[#5B3DF5] hover:opacity-90'
                    : 'cursor-not-allowed bg-[#C7C9D1]'
                }`}
              >
                I Agree
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

const mapDispatchToProps = {
  triggerLogout: logOut,
  triggerEulaConfirm: eulaConfirm
}

export default connect(null, mapDispatchToProps)(Eula);

